import { useQuery } from '@tanstack/react-query';
import { useApiClient } from 'utils/apiClient';
import {
  TipPerTransactionReportParams,
  TipsCSVReportParams,
  TipsSummaryReportParams,
} from 'utils/apiClient/services/reports/types';
import { downloadFile } from 'utils/api';
import { useIntl } from 'react-intl';
import { handleErrorNotification } from 'Common/utils';

/* eslint-disable import/prefer-default-export */
export const useTipsSummary = ({
  tableParams,
  enabled,
}: {
  tableParams: TipsSummaryReportParams;
  enabled: boolean;
}) => {
  const apiClient = useApiClient();
  const intl = useIntl();
  return useQuery({
    queryKey: ['reports', 'tips-summary', tableParams],
    queryFn: async () => {
      try {
        const data = await apiClient.Reports.loadTipsSummaryReport(tableParams);
        return data;
      } catch (error: any) {
        if (error.response && error.response.status === 400) {
          handleErrorNotification({
            error,
            lang: intl.locale.toLowerCase(),
          });
        }
      }
      return null;
    },
    enabled,
    staleTime: 1500,
    refetchOnWindowFocus: false,
  });
};

export const useTipsPerTransaction = ({
  tableParams,
  enabled,
}: {
  tableParams: TipPerTransactionReportParams;
  enabled: boolean;
}) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['reports', 'tips-list', tableParams],
    queryFn: () => apiClient.Reports.loadTipsListReport(tableParams),
    enabled,
    staleTime: 1500,
    refetchOnWindowFocus: false,
  });
};

export const useTipsCSVReport = ({
  accountId,
  group_by,
  user_id,
  created_at_start,
  created_at_end,
  report_type,
}: TipsCSVReportParams) => {
  const apiClient = useApiClient();
  const fileNameMap = {
    list: 'tip_list',
    summary: 'tip_summary',
  };
  const { locale } = useIntl();
  return useQuery({
    queryKey: [
      'reports',
      'tips-csv-report',
      accountId,
      created_at_start,
      created_at_end,
      report_type,
    ],
    queryFn: async () => {
      const data = await apiClient.Reports.createTipsCSVReport({
        accountId,
        group_by,
        user_id,
        created_at_start,
        created_at_end,
        report_type,
        locale,
      });
      const fileNameSuffix = `${accountId}_${created_at_start.format(
        'YYYYMMDD'
      )}_${created_at_end.format('YYYYMMDD')}`;
      const fileName = fileNameMap[report_type];
      downloadFile(data, `${fileName}_${fileNameSuffix}`, 'csv');
    },
    refetchOnWindowFocus: false,
    enabled: false,
  });
};
